import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Shield,
  Globe,
  Users,
  Award,
  BarChart3,
  ArrowRight,
  CheckCircle,
  Star,
  Quote,
  Bitcoin,
  PieChart,
  Target,
  Zap,
  Lock,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Calculator,
  Clock,
  Percent
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const Homepage = () => {
  const [currentStat, setCurrentStat] = useState(0);
  const [cryptoData, setCryptoData] = useState([
    { name: 'Bitcoin', symbol: 'BTC', price: '$67,234', change: '+2.45%', positive: true },
    { name: 'Ethereum', symbol: 'ETH', price: '$3,891', change: '+1.82%', positive: true },
    { name: 'Solana', symbol: 'SOL', price: '$178', change: '-0.73%', positive: false },
    { name: 'Cardano', symbol: 'ADA', price: '$0.52', change: '+3.21%', positive: true }
  ]);
  const [calculatorData, setCalculatorData] = useState({
    selectedPlan: 0,
    investmentAmount: 1000,
    duration: 21
  });

  const stats = useMemo(
    () => [
      { value: '$2.8B', label: 'Assets under management', icon: TrendingUp },
      { value: '15,000+', label: 'Active investors', icon: Users },
      { value: '94%', label: 'Client satisfaction', icon: Award },
      { value: '25+', label: 'Years experience', icon: BarChart3 }
    ],
    []
  );

  const services = useMemo(
    () => [
      {
        icon: Bitcoin,
        title: 'Digital asset programs',
        description:
          'Institutional-style workflows for onboarding, monitoring, and reporting across crypto allocations.',
        features: ['Risk budgeting', 'Operational controls', 'Portfolio reporting']
      },
      {
        icon: PieChart,
        title: 'Portfolio construction',
        description:
          'Diversified sleeves designed around liquidity needs, time horizon, and drawdown tolerance.',
        features: ['Asset allocation', 'Scenario analysis', 'Rebalancing discipline']
      },
      {
        icon: Target,
        title: 'Wealth planning',
        description:
          'A coordinated view of cash flows, tax-aware decisions, and long-term capital preservation.',
        features: ['Cashflow modeling', 'Estate alignment', 'Retirement planning']
      },
      {
        icon: Shield,
        title: 'Security & custody posture',
        description:
          'Defense-in-depth controls, least-privilege access patterns, and clear operational accountability.',
        features: ['Cold storage options', 'Multi-party approvals', 'Incident readiness']
      }
    ],
    []
  );

  const investmentPlans = useMemo(
    () => [
      {
        name: 'Standard Plan',
        description: 'Foundational allocation with disciplined reporting and support.',
        price: '$1,000',
        multiplier: 10,
        bonus: '2%',
        duration: '21 days',
        features: ['Structured onboarding', '24/7 email support', 'Monthly statements'],
        popular: false
      },
      {
        name: 'Premium Plan',
        description: 'Higher-touch service with faster response times and deeper analytics.',
        price: '$6,000',
        multiplier: 15,
        bonus: '3%',
        duration: '21 days',
        features: ['Priority support', 'Quarterly reviews', 'Risk dashboards'],
        popular: true
      },
      {
        name: 'Advanced Plan',
        description: 'Dedicated oversight for complex balances and multi-entity needs.',
        price: '$10,000',
        multiplier: 20,
        bonus: '5%',
        duration: '21 days',
        features: ['Dedicated manager', 'Custom reporting', 'Concierge workflows'],
        popular: false
      },
      {
        name: 'Platinum Plan',
        description: 'Elite service tier for investors who want end-to-end coordination.',
        price: '$25,000',
        multiplier: 25,
        bonus: '7%',
        duration: '21 days',
        features: ['White-glove onboarding', 'Private briefings', 'Tailored mandates'],
        popular: false
      },
      {
        name: 'Diamond Plan',
        description: 'Executive-level partnership with embedded operational support.',
        price: '$50,000',
        multiplier: 30,
        bonus: '10%',
        duration: '21 days',
        features: ['Executive team', 'Custom mandate design', 'Global coverage'],
        popular: false
      }
    ],
    []
  );

  const testimonials = useMemo(
    () => [
      {
        name: 'Javier Elstrom',
        role: 'Portfolio Strategist',
        content:
          'The reporting cadence is what sold me: clear, consistent, and easy to audit when stakeholders ask questions.',
        rating: 5
      },
      {
        name: 'Aisha Banzi',
        role: 'Family Office CFO',
        content:
          'We needed a partner who could speak both "portfolio" and "operations." The team made the handoff painless.',
        rating: 5
      },
      {
        name: 'Noah Feldspar',
        role: 'Corporate Treasurer',
        content:
          'We onboarded quickly, and the dashboard finally matches how finance actually thinks about risk.',
        rating: 5
      }
    ],
    []
  );

  useEffect(() => {
    const fetchCryptoData = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,cardano&vs_currencies=usd&include_24hr_change=true'
        );
        const data = await response.json();

        const newCryptoData = [
          {
            name: 'Bitcoin',
            symbol: 'BTC',
            price: `$${data.bitcoin.usd.toLocaleString()}`,
            change: `${data.bitcoin.usd_24h_change > 0 ? '+' : ''}${data.bitcoin.usd_24h_change.toFixed(2)}%`,
            positive: data.bitcoin.usd_24h_change > 0
          },
          {
            name: 'Ethereum',
            symbol: 'ETH',
            price: `$${data.ethereum.usd.toLocaleString()}`,
            change: `${data.ethereum.usd_24h_change > 0 ? '+' : ''}${data.ethereum.usd_24h_change.toFixed(2)}%`,
            positive: data.ethereum.usd_24h_change > 0
          },
          {
            name: 'Solana',
            symbol: 'SOL',
            price: `$${data.solana.usd.toLocaleString()}`,
            change: `${data.solana.usd_24h_change > 0 ? '+' : ''}${data.solana.usd_24h_change.toFixed(2)}%`,
            positive: data.solana.usd_24h_change > 0
          },
          {
            name: 'Cardano',
            symbol: 'ADA',
            price: `$${data.cardano.usd.toLocaleString()}`,
            change: `${data.cardano.usd_24h_change > 0 ? '+' : ''}${data.cardano.usd_24h_change.toFixed(2)}%`,
            positive: data.cardano.usd_24h_change > 0
          }
        ];

        setCryptoData(newCryptoData);
      } catch (error) {
        // Keep fallback values if the network request fails
      }
    };

    fetchCryptoData();
    const interval = setInterval(fetchCryptoData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stats.length]);

  const calculateReturns = () => {
    const plan = investmentPlans[calculatorData.selectedPlan];
    const baseReturn = calculatorData.investmentAmount * plan.multiplier;
    const bonus = calculatorData.investmentAmount * (parseInt(plan.bonus, 10) / 100);
    const totalReturn = baseReturn + bonus;
    const profit = totalReturn - calculatorData.investmentAmount;

    return {
      baseReturn,
      bonus,
      totalReturn,
      profit,
      roi: ((profit / calculatorData.investmentAmount) * 100).toFixed(1)
    };
  };

  const returns = calculateReturns();

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative border-b border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_0%,rgba(34,211,238,0.16),transparent_55%),radial-gradient(900px_circle_at_90%_10%,rgba(56,189,248,0.10),transparent_55%)]" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
            <div className="grid items-start gap-12 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold tracking-wide text-stone-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                  Wealth management, portfolio construction, and investor reporting
                </div>

                <h1 className="mt-6 text-4xl font-semibold tracking-tight text-white md:text-6xl">
                  Grow your wealth{' '}
                  <span className="text-cyan-200">with a plan that fits you</span>
                </h1>

                <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
                  Solentra is for people who want their money working harder, without the buzzwords. We help you choose a
                  sensible path, stay diversified, and understand what is happening in your account, especially when
                  markets get loud.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center rounded-xl bg-cyan-400 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors"
                  >
                    Get started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                  <Link
                    to="/investment-plans"
                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors"
                  >
                    See our plans
                  </Link>
                </div>

                <dl className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-stone-950/40 p-4">
                    <dt className="text-xs font-semibold tracking-wide text-slate-400">Guidance</dt>
                    <dd className="mt-1 text-sm font-semibold text-white">Real people when you need them</dd>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-stone-950/40 p-4">
                    <dt className="text-xs font-semibold tracking-wide text-slate-400">Balance</dt>
                    <dd className="mt-1 text-sm font-semibold text-white">Risk you can sleep with</dd>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-stone-950/40 p-4 sm:col-span-1 col-span-2">
                    <dt className="text-xs font-semibold tracking-wide text-slate-400">Updates</dt>
                    <dd className="mt-1 text-sm font-semibold text-white">Clear summaries, not fine print</dd>
                  </div>
                </dl>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-3xl border border-white/10 bg-stone-950/40 p-6 shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="text-xs font-semibold tracking-wide text-slate-400">Live prices</div>
                      <div className="mt-2 text-lg font-semibold text-white">Popular crypto (USD)</div>
                      <div className="mt-1 text-sm text-slate-400">Refreshes about every 30 seconds</div>
                    </div>
                    <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2 text-xs font-semibold text-cyan-100">
                      Live
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {cryptoData.slice(0, 4).map((crypto) => (
                      <div
                        key={crypto.symbol}
                        className="rounded-2xl border border-white/10 bg-white/5 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold tracking-wide text-slate-400">{crypto.symbol}</div>
                          <div
                            className={`text-xs font-semibold tabular-nums ${
                              crypto.positive ? 'text-emerald-300' : 'text-rose-300'
                            }`}
                          >
                            {crypto.change}
                          </div>
                        </div>
                        <div className="mt-2 text-lg font-semibold tracking-tight text-white tabular-nums">
                          {crypto.price}
                        </div>
                        <div className="mt-1 text-xs text-slate-400">{crypto.name}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-white/10 bg-stone-950/30 p-4">
                      <div className="text-xs text-slate-400">Getting started</div>
                      <div className="mt-1 text-sm font-semibold text-white">Quick and friendly</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-stone-950/30 p-4">
                      <div className="text-xs text-slate-400">Transparency</div>
                      <div className="mt-1 text-sm font-semibold text-white">No mystery fees</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-stone-950/30 p-4">
                      <div className="text-xs text-slate-400">Your dashboard</div>
                      <div className="mt-1 text-sm font-semibold text-white">Easy to read at a glance</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="border-b border-white/10 py-14 md:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                const active = currentStat === index;
                return (
                  <div
                    key={stat.label}
                    className={`rounded-3xl border p-6 transition-colors ${
                      active
                        ? 'border-cyan-400/35 bg-cyan-400/5'
                        : 'border-white/10 bg-stone-950/40 hover:border-white/15'
                    }`}
                  >
                    <Icon className="h-6 w-6 text-cyan-300" />
                    <div className="mt-4 text-3xl font-semibold tracking-tight text-white tabular-nums">{stat.value}</div>
                    <div className="mt-2 text-sm text-slate-400">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">What we build with you</h2>
              <p className="mt-4 text-base text-slate-300 md:text-lg">
                A modular stack of services so you can scale from a single mandate to a full operating footprint.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {services.map((service) => {
                const Icon = service.icon;
                return (
                  <div key={service.title} className="card-glass card-hover rounded-3xl p-6">
                    <Icon className="h-7 w-7 text-cyan-300" />
                    <h3 className="mt-5 text-lg font-semibold text-white">{service.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-300">{service.description}</p>
                    <ul className="mt-5 space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm text-stone-200">
                          <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-300" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Plans preview */}
        <section className="border-t border-white/10 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div className="max-w-2xl">
                <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Programs, priced for intent</h2>
                <p className="mt-4 text-base text-slate-300 md:text-lg">
                  Start where you are, then upgrade tiers as your balance, reporting needs, and complexity grow.
                </p>
              </div>
              <Link
                to="/investment-plans"
                className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors"
              >
                View all plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-3">
              {investmentPlans.slice(0, 3).map((plan) => (
                <div
                  key={plan.name}
                  className={`relative rounded-3xl border p-7 ${
                    plan.popular ? 'border-cyan-400/35 bg-cyan-400/5' : 'border-white/10 bg-stone-950/40'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 right-6 rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-xs font-semibold text-cyan-100">
                      Most selected
                    </div>
                  )}

                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                    <div className="text-right">
                      <div className="text-2xl font-semibold tracking-tight text-white tabular-nums">{plan.price}</div>
                      <div className="text-xs text-slate-400">minimum</div>
                    </div>
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-slate-300">{plan.description}</p>

                  <div className="mt-6 space-y-2">
                    {plan.features.slice(0, 3).map((feature) => (
                      <div key={feature} className="flex items-start gap-2 text-sm text-stone-200">
                        <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-300" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-7 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Return multiplier</span>
                      <span className="font-semibold text-cyan-200 tabular-nums">{plan.multiplier}x</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-slate-400">Welcome bonus</span>
                      <span className="font-semibold text-cyan-200 tabular-nums">{plan.bonus}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-slate-400">Duration</span>
                      <span className="font-semibold text-stone-100">{plan.duration}</span>
                    </div>
                  </div>

                  <Link
                    to="/signup"
                    className={`mt-6 inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-cyan-400 text-slate-950 hover:bg-cyan-300'
                        : 'border border-white/15 bg-white/5 text-stone-100 hover:bg-white/10'
                    }`}
                  >
                    Get started
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why */}
        <section className="border-t border-white/10 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-6">
                <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Why people stay with Solentra</h2>
                <div className="mt-8 space-y-6">
                  {[
                    { icon: Zap, title: 'Operational velocity', desc: 'Fast decisions, clean approvals, and crisp reporting cycles.' },
                    { icon: Lock, title: 'Institutional discipline', desc: 'Controls-first workflows designed for real money at scale.' },
                    { icon: Users, title: 'Human expertise', desc: 'Specialists who can translate markets into actionable plans.' },
                    { icon: Globe, title: 'Global perspective', desc: 'A framework that works across jurisdictions and time zones.' }
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.title} className="flex gap-4">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-400/10">
                          <Icon className="h-5 w-5 text-cyan-100" />
                        </div>
                        <div>
                          <div className="text-base font-semibold text-white">{item.title}</div>
                          <div className="mt-1 text-sm leading-relaxed text-slate-300">{item.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-3xl border border-white/10 bg-stone-950/40 p-8">
                  <div className="text-sm font-semibold tracking-wide text-slate-300">Illustrative performance snapshot</div>
                  <div className="mt-2 text-xs text-slate-500">For demonstration purposes only - not a promise of future results.</div>

                  <div className="mt-8 space-y-4">
                    {[
                      { label: '1Y return', value: '+87.3%' },
                      { label: '3Y return', value: '+245.8%' },
                      { label: '5Y return', value: '+512.4%' },
                      { label: 'Sharpe (illustrative)', value: '2.34' }
                    ].map((row) => (
                      <div key={row.label} className="flex items-center justify-between border-b border-white/10 py-3 last:border-b-0">
                        <div className="text-sm text-slate-300">{row.label}</div>
                        <div className="text-sm font-semibold tabular-nums text-cyan-200">{row.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calculator */}
        <section className="border-t border-white/10 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Scenario calculator</h2>
              <p className="mt-4 text-base text-slate-300 md:text-lg">
                Model a simple scenario in seconds. This is a planning tool, not a forecast of realized outcomes.
              </p>
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <div className="rounded-3xl border border-white/10 bg-stone-950/40 p-6">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white">
                    <Calculator className="h-4 w-4 text-cyan-300" />
                    Inputs
                  </div>

                  <div className="mt-6 space-y-5">
                    <div>
                      <label className="text-xs font-semibold tracking-wide text-slate-400">Program</label>
                      <select
                        value={calculatorData.selectedPlan}
                        onChange={(e) =>
                          setCalculatorData({ ...calculatorData, selectedPlan: parseInt(e.target.value, 10) })
                        }
                        className="mt-2 w-full rounded-xl border border-white/10 bg-stone-950/40 px-3 py-3 text-sm text-stone-100 outline-none focus:border-cyan-400/50"
                      >
                        {investmentPlans.map((plan, index) => (
                          <option key={plan.name} value={index}>
                            {plan.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-semibold tracking-wide text-slate-400">Investment amount (USD)</label>
                      <input
                        type="number"
                        value={calculatorData.investmentAmount}
                        onChange={(e) =>
                          setCalculatorData({
                            ...calculatorData,
                            investmentAmount: parseInt(e.target.value || '0', 10)
                          })
                        }
                        className="mt-2 w-full rounded-xl border border-white/10 bg-stone-950/40 px-3 py-3 text-sm text-stone-100 outline-none focus:border-cyan-400/50 tabular-nums"
                        min="1000"
                        step="1000"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold tracking-wide text-slate-400">Duration (days)</label>
                      <input
                        type="number"
                        value={calculatorData.duration}
                        onChange={(e) =>
                          setCalculatorData({
                            ...calculatorData,
                            duration: parseInt(e.target.value || '0', 10)
                          })
                        }
                        className="mt-2 w-full rounded-xl border border-white/10 bg-stone-950/40 px-3 py-3 text-sm text-stone-100 outline-none focus:border-cyan-400/50 tabular-nums"
                        min="21"
                        max="365"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-3xl border border-emerald-400/20 bg-emerald-400/5 p-6">
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-100">
                      <TrendingUp className="h-4 w-4" />
                      Total modeled output
                    </div>
                    <div className="mt-4 text-4xl font-semibold tracking-tight text-emerald-200 tabular-nums">
                      ${returns.totalReturn.toLocaleString()}
                    </div>
                    <div className="mt-2 text-sm text-slate-300">After {calculatorData.duration} days</div>
                  </div>

                  <div className="rounded-3xl border border-cyan-400/20 bg-cyan-400/5 p-6">
                    <div className="flex items-center gap-2 text-sm font-semibold text-cyan-100">
                      <Percent className="h-4 w-4" />
                      ROI (modeled)
                    </div>
                    <div className="mt-4 text-4xl font-semibold tracking-tight text-cyan-100 tabular-nums">{returns.roi}%</div>
                    <div className="mt-2 text-sm text-slate-300">Return on investment</div>
                  </div>

                  <div className="rounded-3xl border border-white/10 bg-stone-950/40 p-6 md:col-span-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-white">
                      <DollarSign className="h-4 w-4 text-cyan-300" />
                      Breakdown
                    </div>
                    <div className="mt-5 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="text-xs text-slate-400">Base component</div>
                        <div className="mt-2 text-lg font-semibold tabular-nums text-stone-100">
                          ${returns.baseReturn.toLocaleString()}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="text-xs text-slate-400">Bonus component</div>
                        <div className="mt-2 text-lg font-semibold tabular-nums text-stone-100">
                          ${returns.bonus.toLocaleString()}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="text-xs text-slate-400">Modeled profit</div>
                        <div className="mt-2 text-lg font-semibold tabular-nums text-stone-100">
                          ${returns.profit.toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Clock className="h-4 w-4 text-cyan-300" />
                        Avg. modeled daily increment
                      </div>
                      <div className="text-sm font-semibold tabular-nums text-white">
                        ${(returns.profit / Math.max(calculatorData.duration, 1)).toFixed(0)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="border-t border-white/10 py-14 md:py-20" id="testimonials">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">What our clients say</h2>
              <p className="mt-4 text-base text-slate-300 md:text-lg">
                Busy professionals and families who wanted straight answers, steady communication, and fewer surprises.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {testimonials.map((t) => (
                <div key={t.name} className="rounded-3xl border border-white/10 bg-stone-950/40 p-7">
                  <Quote className="h-6 w-6 text-cyan-300" />
                  <p className="mt-5 text-sm leading-relaxed text-stone-200">"{t.content}"</p>
                  <div className="mt-6 flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-white">{t.name}</div>
                      <div className="text-xs text-slate-400">{t.role}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(t.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-amber-300" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-white/10 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center">
              <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Curious if we are a good fit?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300 md:text-lg">
                Open an account when you are ready, or talk to us first. Either way, we will walk you through the plan,
                the fees, and what to expect before you move a dollar.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/signup"
                  className="inline-flex w-full items-center justify-center rounded-xl bg-cyan-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors sm:w-auto"
                >
                  Create your account
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex w-full items-center justify-center rounded-xl border border-white/15 bg-white/5 px-8 py-3 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors sm:w-auto"
                >
                  Ask us anything
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="border-t border-white/10 py-14 md:py-20" id="contact">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-5">
                <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Contact</h2>
                <p className="mt-4 text-base text-slate-300 md:text-lg">
                  Prefer email? Want a same-day call? Use whichever channel fits your workflow.
                </p>

                <div className="mt-8 space-y-4 text-sm">
                  <div className="flex items-center gap-3 text-stone-200">
                    <Phone className="h-5 w-5 text-cyan-300" />
                    <span className="tabular-nums">(657) 588-4977</span>
                  </div>
                  <div className="flex items-center gap-3 text-stone-200">
                    <Mail className="h-5 w-5 text-cyan-300" />
                    <span>invest@solentrawealth.com</span>
                  </div>
                  <div className="flex items-start gap-3 text-stone-200">
                    <MapPin className="mt-0.5 h-5 w-5 text-cyan-300" />
                    <span>1301 Sansome st, San Francisco, CA 94111</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-3xl border border-white/10 bg-stone-950/40 p-8">
                  <div className="text-sm font-semibold text-white">Quick message</div>

                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      placeholder="Your name"
                      className="w-full rounded-xl border border-white/10 bg-stone-950/40 px-4 py-3 text-sm text-stone-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/50"
                    />
                    <input
                      type="email"
                      placeholder="Your email"
                      className="w-full rounded-xl border border-white/10 bg-stone-950/40 px-4 py-3 text-sm text-stone-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/50"
                    />
                  </div>
                  <textarea
                    placeholder="How can we help?"
                    rows="5"
                    className="mt-4 w-full rounded-xl border border-white/10 bg-stone-950/40 px-4 py-3 text-sm text-stone-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/50"
                  />
                  <button
                    type="button"
                    className="mt-4 w-full rounded-xl bg-cyan-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors"
                  >
                    Send message
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Homepage;
